import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { useApp } from '../context/AppContext'

const { FiPlus, FiBook, FiEdit3, FiTrash2, FiDownload, FiArchive, FiLock, FiChevronDown, FiChevronUp, FiFileText, FiEye, FiX, FiClock, FiCheckCircle, FiUser, FiCopy } = FiIcons

const CourseSelector = ({ courses, activeCourse, onSelectCourse, onCreateCourse, onDeleteCourse, onCompleteReport }) => {
  const { courseService, dispatch, state } = useApp()
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState({
    draft: true,
    active: true,
    finalized: true
  })
  const [newCourse, setNewCourse] = useState({
    faculty: '',
    programme: '',
    courseName: '',
    courseCode: '',
    creditHours: '',
    semester: '',
    session: '',
    status: 'draft'  // Set default status to 'draft'
  })
  const [showPdfPreview, setShowPdfPreview] = useState(null)
  const [courseCreators, setCourseCreators] = useState({})
  
  // Duplicate course state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [courseToDuplicate, setCourseToDuplicate] = useState(null)
  const [duplicateCourseName, setDuplicateCourseName] = useState('')
  const [duplicateCreatorName, setDuplicateCreatorName] = useState('')
  const [duplicateLoading, setDuplicateLoading] = useState(false)

  // Get course creators on component mount
  useEffect(() => {
    async function fetchCourseCreators() {
      if (courses && courses.length > 0) {
        try {
          const { data } = await courseService.getCourseCreators();
          if (data) {
            const creatorsMap = {};
            data.forEach(item => {
              creatorsMap[item.course_id] = item.user_name || item.email;
            });
            setCourseCreators(creatorsMap);
          }
        } catch (error) {
          console.error("Error fetching course creators:", error);
        }
      }
    }

    fetchCourseCreators();
  }, [courses, courseService]);

  // Group courses by status
  const groupedCourses = courses.reduce((groups, course) => {
    const status = course.status === 'finalized' ? 'finalized' : 
                  course.status === 'active' ? 'active' : 'draft';
    if (!groups[status]) groups[status] = [];
    groups[status].push(course);
    return groups;
  }, { draft: [], active: [], finalized: [] });

  // Pre-defined semester options
  const semesterOptions = [
    'Sem I',
    'Sem II', 
    'Short Sem'
  ]

  // Generate session options (current year and next few years)
  const generateSessionOptions = () => {
    const currentYear = new Date().getFullYear()
    const sessions = []
    
    for (let i = -2; i <= 3; i++) {
      const startYear = currentYear + i
      const endYear = startYear + 1
      sessions.push(`${startYear}/${endYear}`)
    }
    
    return sessions
  }

  // Format date with time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    
    // Format date part: DD/MM/YYYY
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    // Format time part: HH:MM AM/PM
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    
    // Convert hours from 24h to 12h format
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    
    return `${day}/${month}/${year}, ${hours}:${minutes} ${ampm}`;
  }

  const sessionOptions = generateSessionOptions()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const { data, error } = await courseService.createCourse({
        ...newCourse,
        creditHours: parseInt(newCourse.creditHours)
      })

      if (error) throw new Error(error)

      dispatch({ type: 'ADD_COURSE', payload: data })
      onSelectCourse(data)
      setNewCourse({
        faculty: '',
        programme: '',
        courseName: '',
        courseCode: '',
        creditHours: '',
        semester: '',
        session: '',
        status: 'draft'  // Reset status to 'draft'
      })
      setShowCreateForm(false)
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (courseId) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      try {
        const { error } = await courseService.deleteCourse(courseId)
        if (error) throw new Error(error)

        dispatch({ type: 'DELETE_COURSE', payload: courseId })
        onDeleteCourse && onDeleteCourse(courseId)
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: error.message })
      }
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setNewCourse(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  }

  const handleViewReport = (report) => {
    setShowPdfPreview(report);
  }

  const getProgressColor = (progress) => {
    if (progress < 30) return 'bg-red-500'
    if (progress < 70) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'archived':
        return 'bg-blue-100 text-blue-800'
      case 'finalized':
        return 'bg-purple-100 text-purple-800'
      case 'active':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-yellow-100 text-yellow-800'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'finalized':
        return FiLock
      case 'archived':
        return FiArchive
      case 'active':
        return FiCheckCircle
      default:
        return FiEdit3
    }
  }

  // IMPROVED: Get creator name for a course - fix for "Unknown" issue
  const getCreatorName = (courseId, course) => {
    // Priority 1: Check if the current user is the creator of this course
    // This is especially useful for newly created courses that may not be in courseCreators map yet
    if (state.user && course.user_id === state.user.id && state.profile) {
      return state.profile.name || state.user.email?.split('@')[0] || 'Unknown';
    }
    
    // Priority 2: Use our pre-fetched courseCreators map
    if (courseCreators[courseId]) {
      return courseCreators[courseId];
    }
    
    // Priority 3: Return Unknown if all else fails
    return 'Unknown';
  }

  // Handle opening duplicate course modal
  const handleOpenDuplicateModal = (e, course) => {
    e.stopPropagation();
    setCourseToDuplicate(course);
    setDuplicateCourseName(`${course.course_name || course.courseName} (Copy)`);
    // Set default creator name to current user's name
    if (state.profile && state.profile.name) {
      setDuplicateCreatorName(state.profile.name);
    } else if (state.user && state.user.email) {
      setDuplicateCreatorName(state.user.email.split('@')[0]);
    } else {
      setDuplicateCreatorName('');
    }
    setShowDuplicateModal(true);
  }

  // Handle duplicate course submission
  const handleDuplicateCourse = async () => {
    if (!courseToDuplicate || !duplicateCourseName.trim()) return;
    
    setDuplicateLoading(true);
    try {
      const { data, error } = await courseService.duplicateCourse(
        courseToDuplicate.id,
        duplicateCourseName.trim(),
        duplicateCreatorName.trim()
      );
      
      if (error) throw new Error(error);
      
      // Add the new course to the state
      dispatch({ type: 'ADD_COURSE', payload: data });
      
      // Close the modal and reset states
      setShowDuplicateModal(false);
      setCourseToDuplicate(null);
      setDuplicateCourseName('');
      setDuplicateCreatorName('');
      
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      alert(`Failed to duplicate course: ${error.message}`);
    } finally {
      setDuplicateLoading(false);
    }
  }

  // Handle viewing complete report using same implementation as ArchivedReports
  const handleViewCompleteReport = (course) => {
    // Check if complete_report exists in the course object
    if (course.complete_report) {
      const reportWindow = window.open('', '_blank');
      
      // Extract report data
      const report = { 
        report: course.complete_report,
        courseCode: course.course_code || course.courseCode,
        programme: course.programme,
        semester: course.semester,
        session: course.session
      };
      
      // Get creator name for this course
      const creatorName = getCreatorName(course.id, course);
      
      // Get finalized date and time
      const finalizedAt = course.finalized_at || course.completed_at;
      const finalizedDateTime = formatDateTime(finalizedAt);
      
      const totalStudents = report.report.gradeDistribution
        ? report.report.gradeDistribution.reduce((sum, grade) => sum + (grade.count || 0), 0)
        : 0;
      
      // Extract bell curve analysis data from the report
      const bell = report.report.bellCurveAnalysis || {};
      const total = bell.total || 1; // avoid division by zero

      const actual = [
        bell.lowGrades || 0,
        bell.midGrades || 0,
        bell.highGrades || 0
      ];
      // optimal as raw counts for comparison
      const optimal = [
        Math.round(total * 0.15),
        Math.round(total * 0.7),
        Math.round(total * 0.15)
      ];
      const labels = ['Below Avg', 'Average', 'High'];
      const colors = ['#dc2626', '#2563eb', '#059669'];

      const maxY = Math.max(...actual, ...optimal, 1);
      const barWidth = 28;
      const barGap = 32;
      const chartHeight = 120;
      const chartWidth = 2 * barGap + 3 * (barWidth * 2 + 4);

      const barChartSection = `
        <div style="margin: 24px 0;">
          <div style="font-weight: bold; margin-bottom: 8px;">Bell Curve: Actual vs. Optimal</div>
          <svg width="${chartWidth}" height="${chartHeight + 30}">
            ${actual.map((val, i) => {
              // Bar positions
              const xActual = barGap + i * (barWidth * 2 + barGap);
              const xOptimal = xActual + barWidth + 4;

              // Heights
              const hActual = (val / maxY) * chartHeight;
              const hOptimal = (optimal[i] / maxY) * chartHeight;

              // Y positions
              const yActual = chartHeight - hActual + 20;
              const yOptimal = chartHeight - hOptimal + 20;

              return `
                <!-- Actual Bar -->
                <rect x="${xActual}" y="${yActual}" width="${barWidth}" height="${hActual}" fill="${colors[i]}" rx="3"/>
                <!-- Actual Label -->
                <text x="${xActual + barWidth/2}" y="${yActual - 6}" text-anchor="middle" font-size="12" fill="#333">${val}</text>
                <!-- Optimal Bar -->
                <rect x="${xOptimal}" y="${yOptimal}" width="${barWidth}" height="${hOptimal}" fill="#a3a3a3" rx="3"/>
                <!-- Optimal Label -->
                <text x="${xOptimal + barWidth/2}" y="${yOptimal - 6}" text-anchor="middle" font-size="12" fill="#333">${optimal[i]}</text>
                <!-- Category -->
                <text x="${xActual + barWidth + 2}" y="${chartHeight + 28}" text-anchor="middle" font-size="13" fill="#222">${labels[i]}</text>
              `;
            }).join('')}
            <!-- Legends -->
            <rect x="6" y="8" width="16" height="12" fill="#a3a3a3"/>
            <text x="26" y="18" font-size="12" fill="#222">Optimal</text>
            <rect x="80" y="8" width="16" height="12" fill="#2563eb"/>
            <text x="100" y="18" font-size="12" fill="#222">Actual</text>
          </svg>
          <div style="font-size: 12px; color: #666; margin-top: 4px;">Each group: left bar = actual, right bar = optimal</div>
        </div>
      `;

      // Now you can use bellCurveSection in your full report HTML
      reportWindow.document.write(`
        <html>
          <head>
            <title>Complete Report - ${report.courseCode}</title>
            <style>
              /* 1. Modern Font and Background */
              body {
                font-family: 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
                background: #f8fafc;
                color: #222;
                margin: 0;
                padding: 0;
              }
              /* 2. Card-like Report Container */
              .report-container {
                border-radius: 14px;
                box-shadow: 0 4px 24px rgba(16,30,54,0.09);
                background: #fff;
                padding: 36px 18px 60px 18px;
                margin: 35px auto;
                max-width: 940px;
                position: relative;
                z-index: 1;
              }
              /* 3. Watermark - Higher, More Subtle, Modern */
              .watermark {
                position: fixed;
                top: 13%;
                left: 50%;
                transform: translate(-50%, -50%);
                opacity: 0.09;
                font-size: 4rem;
                font-weight: 600;
                color: #007cba;
                z-index: 0;
                pointer-events: none;
                white-space: nowrap;
                user-select: none;
                letter-spacing: 0.12em;
              }
              /* 4. Header and Section Style */
              .header {
                border-bottom: 2px solid #e0e7ef;
                padding-bottom: 18px;
                margin-bottom: 26px;
              }
              h1 {
                margin: 0 0 8px 0;
                font-weight: 800;
                color: #1d4ed8;
                font-size: 2.5rem;
                letter-spacing: 0.03em;
              }
              h2 {
                margin-top: 0;
                font-size: 1.5rem;
                color: #1d4ed8;
                letter-spacing: 0.5px;
                border-left: 5px solid #1d4ed8;
                padding-left: 12px;
                background: linear-gradient(90deg,#e0e7ef 70%,#fff 100%);
                border-radius: 4px;
                font-weight: 700;
                margin-bottom: 18px;
              }
              h3 {
                font-size: 1.12rem;
                color: #334155;
                margin-bottom: 6px;
                font-weight: 600;
                border-bottom: 1px solid #e0e7ef;
                padding-bottom: 3px;
              }
              h4 {
                color: #2563eb;
                margin-bottom: 4px;
                margin-top: 18px;
                font-size: 1.02rem;
              }
              /* 5. Card Section */
              .section {
                margin-bottom: 36px;
                background: #fff;
                border-radius: 10px;
                box-shadow: 0 1px 5px rgba(16,30,54,0.07);
                padding: 24px 18px;
                page-break-inside: avoid;
              }
              /* 6. Modern Table */
              table {
                width: 100%;
                border-collapse: separate;
                border-spacing: 0;
                background: #f9fafb;
                border-radius: 8px;
                overflow: hidden;
                margin: 18px 0 4px 0;
                box-shadow: 0 1px 4px rgba(16,30,54,0.05);
              }
              th, td {
                padding: 11px 10px;
                text-align: center;
                font-size: 1.01em;
              }
              th {
                background: #1d4ed8;
                color: #fff;
                font-weight: 600;
                border-bottom: 2px solid #e0e7ef;
              }
              tr:nth-child(even) {
                background: #f1f5f9;
              }
              tr:hover {
                background: #e0e7ef;
              }
              td {
                border-bottom: 1px solid #e0e7ef;
              }
              /* 7. Badges and Statuses */
              .badge {
                display: inline-block;
                padding: 2px 10px;
                border-radius: 11px;
                font-size: 0.95em;
                background: #e0e7ef;
                color: #1d4ed8;
                margin-left: 4px;
                margin-right: 4px;
                font-weight: 500;
                letter-spacing: 0.03em;
              }
              .status-achieved {
                color: #16a34a;
                background-color: #dcfce7;
                padding: 4px 11px;
                border-radius: 5px;
                font-weight: 600;
              }
              .status-not-achieved {
                color: #dc2626;
                background-color: #fee2e2;
                padding: 4px 11px;
                border-radius: 5px;
                font-weight: 600;
              }
              /* 8. Analysis Boxes */
              .bell-curve-analysis {
                background-color: #f0f9ff;
                padding: 20px;
                border-radius: 8px;
                margin: 20px 0;
                box-shadow: 0 1px 4px rgba(16,30,54,0.07);
              }
              .clo-plo-mapping {
                background-color: #fef3c7;
                padding: 15px;
                border-radius: 8px;
                margin: 15px 0;
                box-shadow: 0 1px 4px rgba(16,30,54,0.04);
              }
              /* 9. Signature Section */
              .signature-section {
                margin-top: 35px;
              }
              .signature-box {
                width: 47%;
              }
              .signature-line {
                border-bottom: 1.5px solid #1d4ed8;
                height: 32px;
                margin-bottom: 6px;
                margin-top: 10px;
              }
              .signature-box h3 {
                margin-bottom: 10px;
                color: #2563eb;
                font-size: 1.1rem;
                font-weight: 700;
              }
              /* 10. Footer */
              .report-footer {
                border-top: 1px dashed #b9c3d1;
                margin-top: 32px;
                padding-top: 12px;
                font-size: 13px;
                color: #555;
                display: flex;
                justify-content: space-between;
                align-items: center;
                background: #fafbfc;
                z-index: 2;
                position: relative;
                border-radius: 0 0 10px 10px;
              }
              .no-print {
                margin-top: 28px;
                text-align: center;
              }
              .no-print button {
                padding: 15px 30px;
                background: #1d4ed8;
                color: white;
                border: none;
                border-radius: 6px;
                cursor: pointer;
                font-size: 16px;
                transition: background 0.18s;
                font-weight: 600;
              }
              .no-print button:hover {
                background: #2563eb;
              }
              /* 11. Responsive Design */
              @media (max-width: 700px) {
                .report-container, .section { padding: 12px 4px; }
                table, th, td { font-size: 0.95em; }
                h2 { font-size: 1.14rem; }
                h1 { font-size: 1.5rem; }
              }
              @media print { .no-print { display: none !important; } }
              
              /* 12. Finalized Date Badge */
              .finalized-date-badge {
                background-color: #f0f9ff;
                border: 1px solid #bae6fd;
                border-radius: 6px;
                padding: 6px 12px;
                margin-top: 16px;
                display: inline-flex;
                align-items: center;
                font-size: 0.9rem;
                color: #0369a1;
              }
              
              .finalized-date-badge svg {
                margin-right: 8px;
                width: 16px;
                height: 16px;
              }

              /* 13. Creator Badge */
              .creator-badge {
                background-color: #f0fdf4;
                border: 1px solid #86efac;
                border-radius: 6px;
                padding: 6px 12px;
                margin-top: 10px;
                display: inline-flex;
                align-items: center;
                font-size: 0.9rem;
                color: #166534;
              }
              
              .creator-badge svg {
                margin-right: 8px;
                width: 16px;
                height: 16px;
              }
            </style>
          </head>
          <body>
            <div class="report-container">
              <div class="watermark">CLO-PLO Mapper</div>
              <div class="header">
                <h1>COMPREHENSIVE COURSE REPORT</h1>
                <h2>${report.report.courseInfo.courseName} (${report.report.courseInfo.courseCode})</h2>
                <div style="display: flex; justify-content: space-between; margin-top: 20px;">
                  <div>
                    <p><strong>Faculty:</strong> ${report.report.courseInfo.faculty}</p>
                    <p><strong>Programme:</strong> ${report.programme}</p>
                    <p><strong>Semester:</strong> ${report.semester}</p>
                    <p><strong>Session:</strong> ${report.session}</p>
                  </div>
                  <div>
                    <p><strong>Credit Hours:</strong> ${report.report.courseInfo.creditHours}</p>
                    <p><strong>Report Generated:</strong> ${report.report.courseInfo.reportGeneratedDate}</p>
                    <p><strong>Generated By:</strong> ${report.report.courseInfo.reportGeneratedBy}</p>
                    <div class="finalized-date-badge">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                      <span><strong>Finalized:</strong> ${finalizedDateTime}</span>
                    </div>
                    <div class="creator-badge">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      <span><strong>Created by:</strong> ${creatorName}</span>
                    </div>
                  </div>
                </div>
              </div>
              <p style="font-style:italic; color:#007cba; margin-top:0; margin-bottom:24px;">
                Dear ${report.report.courseInfo.coordinatorName || "Course Coordinator"},<br>
                Please find below the comprehensive course analysis report.
              </p>
              <div class="section">
                <h2>1. ASSESSMENT PLAN</h2>
                <table>
                  <tr>
                    <th>Assessment Type</th>
                    <th>Assessment Name</th>
                    <th>Marks (%)</th>
                    <th>CLO Mapping</th>
                    <th>CLO Statement</th>
                    <th>Mapped PLOs</th>
                  </tr>
                  ${report.report.assessmentPlan?.map(assessment => `
                    <tr>
                      <td>${assessment.type}</td>
                      <td>${assessment.name}</td>
                      <td>${assessment.marks}%</td>
                      <td>${assessment.clo}</td>
                      <td>${assessment.cloStatement}</td>
                      <td>${assessment.ploMappings?.map(plo => plo.code).join(', ') || 'None'}</td>
                    </tr>
                  `).join('') || '<tr><td colspan="6">No assessment data available</td></tr>'}
                </table>
              </div>
              <div class="section">
                <h2>2. COURSE LEARNING OUTCOMES (CLO)</h2>
                <table>
                  <tr><th>CLO</th><th>Statement</th></tr>
                  ${Object.entries(report.report.cloStatements || {}).map(([clo, statement]) => `
                    <tr><td>${clo}</td><td>${statement}</td></tr>
                  `).join('') || '<tr><td colspan="2">No CLO statements available</td></tr>'}
                </table>
              </div>
              <div class="section">
                <h2>3. PROGRAMME LEARNING OUTCOMES (PLO)</h2>
                <table>
                  <tr><th>PLO</th><th>Statement</th></tr>
                  ${Object.entries(report.report.ploStatements || {}).map(([plo, statement]) => `
                    <tr><td>${plo}</td><td>${statement}</td></tr>
                  `).join('') || '<tr><td colspan="2">No PLO statements available</td></tr>'}
                </table>
              </div>
              <div class="section">
                <h2>4. CLO-PLO MAPPING</h2>
                <div class="clo-plo-mapping">
                  ${report.report.enhancedCLOPLOMapping?.map(mapping => `
                    <div style="margin-bottom: 15px;">
                      <strong>${mapping.cloCode}:</strong> ${mapping.cloStatement}<br>
                      <span><strong>Mapped PLOs:</strong> ${mapping.mappedPLOs?.map(p => `${p.code} (${p.statement})`).join(', ') || 'None'}</span>
                    </div>
                  `).join('') || '<div>No enhanced CLO-PLO mapping data available</div>'}
                </div>
              </div>
              <div class="section">
                <h2>5. STATISTICAL ANALYSIS</h2>
                <div style="display: flex;">
                  <div style="flex:1">
                    <div style="margin: 10px;"><strong>Average Score:</strong> ${report.report.statistics?.averageScore || 0}%</div>
                    <div style="margin: 10px;"><strong>Highest Score:</strong> ${report.report.statistics?.highestScore || 0}%</div>
                    <div style="margin: 10px;"><strong>Lowest Score:</strong> ${report.report.statistics?.lowestScore || 0}%</div>
                  </div>
                </div>
              </div>
              <div class="section">
                <h2>6. GRADE DISTRIBUTION</h2>
                <table>
                  <tr><th>Grade</th><th>Range (%)</th><th>GPA</th><th>Count</th><th>Percentage</th></tr>
                  ${report.report.gradeDistribution?.map(grade => `
                    <tr>
                      <td>${grade.grade}</td>
                      <td>${grade.min}-${grade.max}</td>
                      <td>${grade.gpa.toFixed(3)}</td>
                      <td>${grade.count}</td>
                      <td>${totalStudents ? ((grade.count / totalStudents) * 100).toFixed(1) : 0}%</td>
                    </tr>
                  `).join('') || '<tr><td colspan="5">No grade distribution data available</td></tr>'}
                </table>
              </div>
              <div class="section">
                <h2>7. BELL CURVE DISTRIBUTION ANALYSIS</h2>
                <div class="bell-curve-analysis">
                  <h3>Distribution Pattern: ${report.report.bellCurveAnalysis?.distributionPattern || 'Not available'}</h3>
                  <div style="display: flex; justify-content: space-between; margin-top: 20px;">
                    <div style="text-align: center;">
                      <strong>High Achievers (A+, A, A-)</strong><br>
                      <span style="font-size: 24px; color: #059669;">${report.report.bellCurveAnalysis?.highGrades || 0}</span><br>
                      <span style="color: #059669;">${report.report.bellCurveAnalysis?.highPercentage || 0}% of students</span>
                    </div>
                    <div style="text-align: center;">
                      <strong>Average Performers (B+, B, B-, C+, C)</strong><br>
                      <span style="font-size: 24px; color: #2563eb;">${report.report.bellCurveAnalysis?.midGrades || 0}</span><br>
                      <span style="color: #2563eb;">${report.report.bellCurveAnalysis?.midPercentage || 0}% of students</span>
                    </div>
                    <div style="text-align: center;">
                      <strong>Below Average (C-, D, F)</strong><br>
                      <span style="font-size: 24px; color: #dc2626;">${report.report.bellCurveAnalysis?.lowGrades || 0}</span><br>
                      <span style="color: #dc2626;">${report.report.bellCurveAnalysis?.lowPercentage || 0}% of students</span>
                    </div>
                  </div>

                  <!-- SVG BAR CHART: Actual vs Optimal -->
                  <div style="margin: 32px 0 8px 0; display: flex; flex-direction: column; align-items: center;">
                    <div style="font-weight: bold; margin-bottom: 8px;">Bell Curve: Actual vs. Optimal</div>
                    <svg width="320" height="150">
                      ${(() => {
                        // Data extraction
                        const bell = report.report.bellCurveAnalysis || {};
                        const total = bell.total || (
                          (bell.highGrades || 0) + (bell.midGrades || 0) + (bell.lowGrades || 0)
                        ) || 1; // avoid division by zero

                        const actual = [
                          bell.lowGrades || 0,
                          bell.midGrades || 0,
                          bell.highGrades || 0
                        ];
                        const labels = ['Below Avg', 'Average', 'High'];
                        const colors = ['#dc2626', '#2563eb', '#059669'];
                        // Optimal as per 15/70/15% split
                        const optimal = [
                          Math.round(total * 0.15),
                          Math.round(total * 0.7),
                          Math.round(total * 0.15)
                        ];
                        const maxY = Math.max(...actual, ...optimal, 1);
                        const barWidth = 26, barGap = 32;
                        // SVG bars
                        return actual.map((val, i) => {
                          const xActual = barGap + i * (barWidth * 2 + barGap);
                          const xOptimal = xActual + barWidth + 4;
                          const hActual = (val / maxY) * 90;
                          const hOptimal = (optimal[i] / maxY) * 90;
                          const yActual = 120 - hActual;
                          const yOptimal = 120 - hOptimal;
                          return `
                            <!-- Actual Bar -->
                            <rect x="${xActual}" y="${yActual}" width="${barWidth}" height="${hActual}" fill="${colors[i]}" rx="3"/>
                            <text x="${xActual + barWidth/2}" y="${yActual - 6}" text-anchor="middle" font-size="12" fill="#222">${val}</text>
                            <!-- Optimal Bar -->
                            <rect x="${xOptimal}" y="${yOptimal}" width="${barWidth}" height="${hOptimal}" fill="#a3a3a3" rx="3"/>
                            <text x="${xOptimal + barWidth/2}" y="${yOptimal - 6}" text-anchor="middle" font-size="12" fill="#222">${optimal[i]}</text>
                            <!-- Label -->
                            <text x="${xActual + barWidth + 2}" y="142" text-anchor="middle" font-size="13" fill="#222">${labels[i]}</text>
                          `;
                        }).join('') +
                        `
                        <!-- Legend -->
                        <rect x="8" y="8" width="16" height="12" fill="#a3a3a3"/>
                        <text x="28" y="18" font-size="12" fill="#222">Optimal</text>
                        <rect x="82" y="8" width="16" height="12" fill="#2563eb"/>
                        <text x="102" y="18" font-size="12" fill="#222">Actual</text>
                        `
                      })()}
                    </svg>
                    <div style="font-size: 12px; color: #666; margin-top: 4px;">Each group: left bar = actual, right bar = optimal</div>
                  </div>
                  <!-- END SVG BAR CHART -->
                </div>
              </div>
              <div class="section">
                <h2>8. CLO ACHIEVEMENT ANALYSIS</h2>
                <table>
                  <tr>
                    <th>CLO</th>
                    <th>CLO Statement</th>
                    <th>Target (%)</th>
                    <th>Achievement (%)</th>
                    <th>Students Achieved</th>
                    <th>Status</th>
                  </tr>
                  ${report.report.cloAchievementData?.map(clo => `
                    <tr>
                      <td>${clo.clo}</td>
                      <td>${clo.statement}</td>
                      <td>${clo.targetPercentage}%</td>
                      <td>${clo.attainment}%</td>
                      <td>${clo.studentsAchieved}/${clo.totalStudents}</td>
                      <td><span class="${clo.status === 'Achieved' ? 'status-achieved' : 'status-not-achieved'}">${clo.status}</span></td>
                    </tr>
                  `).join('') || '<tr><td colspan="6">No CLO achievement data available</td></tr>'}
                </table>
              </div>
              <div class="section">
                <h2>9. PLO ACHIEVEMENT ANALYSIS</h2>
                <h3>Mapped PLOs</h3>
                <table>
                  <tr>
                    <th>PLO</th>
                    <th>PLO Statement</th>
                    <th>Target (%)</th>
                    <th>Achievement (%)</th>
                    <th>Students Achieved</th>
                    <th>Status</th>
                  </tr>
                  ${report.report.ploAchievementData?.mappedPLOs?.map(plo => `
                    <tr>
                      <td>${plo.plo}</td>
                      <td>${plo.description}</td>
                      <td>${plo.targetPercentage}%</td>
                      <td>${plo.attainment}%</td>
                      <td>${plo.studentsAchieved}/${plo.totalStudents}</td>
                      <td><span class="${plo.status === 'Achieved' ? 'status-achieved' : 'status-not-achieved'}">${plo.status}</span></td>
                    </tr>
                  `).join('') || '<tr><td colspan="6">No mapped PLO achievement data available</td></tr>'}
                </table>
              </div>
              <div class="section">
                <h2>10. FAILURE REPORT</h2>
                ${report.report.failureReport?.length ? `
                  <table>
                    <tr><th>Student Name</th><th>Matric No</th><th>Status</th><th>Reason</th></tr>
                    ${report.report.failureReport.map(failure => `
                      <tr>
                        <td>${failure.studentName}</td>
                        <td>${failure.matricNo}</td>
                        <td>${failure.failureStatus}</td>
                        <td>${failure.reason}</td>
                      </tr>
                    `).join('')}
                  </table>
                ` : '<div>No failure report data available.</div>'}
              </div>
              <div class="section">
                <h2>11. ANALYSIS COMMENTS</h2>
                <h3>Course Performance Analysis</h3>
                <p>${report.report.coursePerformance?.comments || 'No comments provided'}</p>
                <h4>Suggestions for Improvement:</h4>
                <ul>
                  ${report.report.coursePerformance?.suggestions?.map(suggestion => `<li>${suggestion}</li>`).join('') || '<li>No suggestions provided</li>'}
                </ul>
                <h3>CLO Analysis</h3>
                <p>${report.report.cloAnalysis?.comments || 'No comments provided'}</p>
                <h4>Suggestions for Improvement:</h4>
                <ul>
                  ${report.report.cloAnalysis?.suggestions?.map(suggestion => `<li>${suggestion}</li>`).join('') || '<li>No suggestions provided</li>'}
                </ul>
                <h3>PLO Analysis</h3>
                <p>${report.report.ploAnalysis?.comments || 'No comments provided'}</p>
                <h4>Suggestions for Improvement:</h4>
                <ul>
                  ${report.report.ploAnalysis?.suggestions?.map(suggestion => `<li>${suggestion}</li>`).join('') || '<li>No suggestions provided</li>'}
                </ul>
              </div>
              <div class="signature-section">
                <h2>12. SIGNATURES</h2>
                <div style="display: flex; justify-content: space-between;">
                  <div className="signature-box">
                    <h3>Course Coordinator</h3>
                    <div className="signature-line"></div>
                    <p><strong>Name:</strong> ${report.report.signatures?.courseCoordinator?.name || '_________________'}</p>
                    <p><strong>Position:</strong> ${report.report.signatures?.courseCoordinator?.position || 'Course Coordinator'}</p>
                    <p><strong>Faculty:</strong> ${report.report.signatures?.courseCoordinator?.faculty || '_________________'}</p>
                    <p><strong>Department:</strong> ${report.report.signatures?.courseCoordinator?.department || '_________________'}</p>
                    <p><strong>Date:</strong> ${report.report.signatures?.courseCoordinator?.signatureDate || '_________________'}</p>
                  </div>
                  <div class="signature-box">
                    <h3>Programme Coordinator</h3>
                    <div class="signature-line"></div>
                    <p><strong>Name:</strong> ${report.report.signatures?.programmeCoordinator?.name || '_________________'}</p>
                    <p><strong>Position:</strong> ${report.report.signatures?.programmeCoordinator?.position || 'Programme Coordinator'}</p>
                    <p><strong>Faculty:</strong> ${report.report.signatures?.programmeCoordinator?.faculty || '_________________'}</p>
                    <p><strong>Department:</strong> ${report.report.signatures?.programmeCoordinator?.department || '_________________'}</p>
                    <p><strong>Date:</strong> ${report.report.signatures?.programmeCoordinator?.signatureDate || '_________________'}</p>
                  </div>
                </div>
              </div>
              <div class="report-footer">
                <span>Generated: ${new Date().toLocaleString()}</span>
                <span>Generated using CLO-PLO Mapper by FAHIM</span>
              </div>
              <div class="no-print" style="margin-top: 30px; text-align: center;">
                <button onclick="window.print()" style="padding: 15px 30px; background: #007cba; color: white; border: none; border-radius: 5px; cursor: pointer; font-size: 16px;">Print Report</button>
              </div>
            </div>
          </body>
        </html>
      `);
      reportWindow.document.close();
    } else {
      alert('Complete report data not available for this course.');
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Course Management</h2>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>New Course</span>
          </button>
        </div>

        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mb-6 p-4 bg-gray-50 rounded-lg border"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Course</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Faculty
                  </label>
                  <input
                    type="text"
                    name="faculty"
                    value={newCourse.faculty}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Faculty of Engineering"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Programme
                  </label>
                  <input
                    type="text"
                    name="programme"
                    value={newCourse.programme}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Computer Science"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Name
                  </label>
                  <input
                    type="text"
                    name="courseName"
                    value={newCourse.courseName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Data Structures"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Code
                  </label>
                  <input
                    type="text"
                    name="courseCode"
                    value={newCourse.courseCode}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., CS201"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Credit Hours
                  </label>
                  <input
                    type="number"
                    name="creditHours"
                    value={newCourse.creditHours}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 3"
                    min="1"
                    max="6"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Semester
                  </label>
                  <select
                    name="semester"
                    value={newCourse.semester}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Semester</option>
                    {semesterOptions.map(sem => (
                      <option key={sem} value={sem}>{sem}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session
                  </label>
                  <select
                    name="session"
                    value={newCourse.session}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Session</option>
                    {sessionOptions.map(session => (
                      <option key={session} value={session}>{session}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Course'}
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Draft Courses Section */}
        <div className="mb-6">
          <div 
            className="flex items-center justify-between bg-yellow-50 p-3 rounded-lg border border-yellow-200 mb-4 cursor-pointer"
            onClick={() => toggleGroup('draft')}
          >
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiEdit3} className="text-yellow-600" />
              <h3 className="text-lg font-semibold text-yellow-900">Draft Courses</h3>
              <span className="bg-yellow-200 text-yellow-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                {groupedCourses.draft?.length || 0}
              </span>
            </div>
            <SafeIcon icon={expandedGroups.draft ? FiChevronUp : FiChevronDown} className="text-yellow-600" />
          </div>

          <AnimatePresence>
            {expandedGroups.draft && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  {groupedCourses.draft && groupedCourses.draft.length > 0 ? (
                    groupedCourses.draft.map((course) => (
                      <motion.div
                        key={course.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          activeCourse?.id === course.id
                            ? 'border-yellow-500 bg-yellow-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => onSelectCourse(course)}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="p-2 rounded-lg bg-yellow-100">
                            <SafeIcon icon={getStatusIcon(course.status)} className="w-5 h-5 text-yellow-600" />
                          </div>
                          <div className="flex space-x-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                onCompleteReport(course)
                              }}
                              className="p-1 text-green-600 hover:text-green-800"
                              title="Complete Report"
                            >
                              <SafeIcon icon={FiDownload} className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDelete(course.id)
                              }}
                              className="p-1 text-red-600 hover:text-red-800"
                              title="Delete Course"
                            >
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        <h3 className="font-semibold text-gray-900 mb-1">
                          {course.course_name || course.courseName}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          {course.course_code || course.courseCode}
                        </p>
                        <p className="text-xs text-gray-500 mb-1">{course.programme}</p>
                        
                        {/* Creator name */}
                        <div className="flex items-center text-xs text-gray-600 mb-2">
                          <SafeIcon icon={FiUser} className="w-3 h-3 mr-1" />
                          <span>Created by: {getCreatorName(course.id, course)}</span>
                        </div>
                        
                        {/* Semester and Session Display */}
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {course.semester}
                          </span>
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {course.session}
                          </span>
                        </div>

                        <div className="mb-2">
                          <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span>Progress</span>
                            <span>{course.progress || 0}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${getProgressColor(course.progress || 0)}`}
                              style={{ width: `${course.progress || 0}%` }}
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800">
                            Draft
                          </span>
                          <span className="text-xs text-gray-500">
                            {course.credit_hours || course.creditHours} CH
                          </span>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="col-span-full text-center py-8">
                      <SafeIcon icon={FiEdit3} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No draft courses. Create a new course to get started.</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Active Courses Section */}
        <div className="mb-6">
          <div 
            className="flex items-center justify-between bg-green-50 p-3 rounded-lg border border-green-200 mb-4 cursor-pointer"
            onClick={() => toggleGroup('active')}
          >
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiCheckCircle} className="text-green-600" />
              <h3 className="text-lg font-semibold text-green-900">Active Courses</h3>
              <span className="bg-green-200 text-green-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                {groupedCourses.active?.length || 0}
              </span>
            </div>
            <SafeIcon icon={expandedGroups.active ? FiChevronUp : FiChevronDown} className="text-green-600" />
          </div>

          <AnimatePresence>
            {expandedGroups.active && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  {groupedCourses.active && groupedCourses.active.length > 0 ? (
                    groupedCourses.active.map((course) => (
                      <motion.div
                        key={course.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          activeCourse?.id === course.id
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => onSelectCourse(course)}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="p-2 rounded-lg bg-green-100">
                            <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600" />
                          </div>
                          <div className="flex space-x-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDuplicateModal(e, course);
                              }}
                              className="p-1 text-blue-600 hover:text-blue-800"
                              title="Duplicate Course"
                            >
                              <SafeIcon icon={FiCopy} className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                onCompleteReport(course)
                              }}
                              className="p-1 text-green-600 hover:text-green-800"
                              title="Complete Report"
                            >
                              <SafeIcon icon={FiDownload} className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDelete(course.id)
                              }}
                              className="p-1 text-red-600 hover:text-red-800"
                              title="Delete Course"
                            >
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        <h3 className="font-semibold text-gray-900 mb-1">
                          {course.course_name || course.courseName}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          {course.course_code || course.courseCode}
                        </p>
                        <p className="text-xs text-gray-500 mb-1">{course.programme}</p>
                        
                        {/* Creator name */}
                        <div className="flex items-center text-xs text-gray-600 mb-2">
                          <SafeIcon icon={FiUser} className="w-3 h-3 mr-1" />
                          <span>Created by: {getCreatorName(course.id, course)}</span>
                        </div>
                        
                        {/* Semester and Session Display */}
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {course.semester}
                          </span>
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {course.session}
                          </span>
                        </div>

                        <div className="mb-2">
                          <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span>Progress</span>
                            <span>{course.progress || 0}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${getProgressColor(course.progress || 0)}`}
                              style={{ width: `${course.progress || 0}%` }}
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                          <span className="text-xs text-gray-500">
                            {course.credit_hours || course.creditHours} CH
                          </span>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="col-span-full text-center py-8">
                      <SafeIcon icon={FiCheckCircle} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No active courses. Activate a course from the draft section.</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Finalized Courses Section */}
        <div>
          <div 
            className="flex items-center justify-between bg-purple-50 p-3 rounded-lg border border-purple-200 mb-4 cursor-pointer"
            onClick={() => toggleGroup('finalized')}
          >
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiLock} className="text-purple-600" />
              <h3 className="text-lg font-semibold text-purple-900">Finalized Courses</h3>
              <span className="bg-purple-200 text-purple-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                {groupedCourses.finalized?.length || 0}
              </span>
            </div>
            <SafeIcon icon={expandedGroups.finalized ? FiChevronUp : FiChevronDown} className="text-purple-600" />
          </div>

          <AnimatePresence>
            {expandedGroups.finalized && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedCourses.finalized && groupedCourses.finalized.length > 0 ? (
                    groupedCourses.finalized.map((course) => (
                      <motion.div
                        key={course.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="p-4 rounded-lg border-2 border-purple-300 bg-purple-50"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="p-2 rounded-lg bg-purple-100">
                            <SafeIcon icon={FiLock} className="w-5 h-5 text-purple-600" />
                          </div>
                          <div className="flex space-x-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(course.id);
                              }}
                              className="p-1 text-red-600 hover:text-red-800"
                              title="Delete Course"
                            >
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        <h3 className="font-semibold text-gray-900 mb-1">
                          {course.course_name || course.courseName}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          {course.course_code || course.courseCode}
                        </p>
                        <p className="text-xs text-gray-500 mb-1">{course.programme}</p>
                        
                        {/* Creator name */}
                        <div className="flex items-center text-xs text-gray-600 mb-2">
                          <SafeIcon icon={FiUser} className="w-3 h-3 mr-1" />
                          <span>Created by: {getCreatorName(course.id, course)}</span>
                        </div>
                        
                        {/* Semester and Session Display */}
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                            {course.semester}
                          </span>
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                            {course.session}
                          </span>
                        </div>

                        {/* Finalized Date and Time */}
                        <div className="mb-3 text-xs text-gray-600 flex items-center">
                          <SafeIcon icon={FiClock} className="w-3 h-3 mr-1 text-purple-500" />
                          <span>Finalized: </span>
                          <span className="font-medium ml-1">
                            {formatDateTime(course.finalized_at || course.completed_at)}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-800">
                            finalized 🔒
                          </span>
                          <span className="text-xs text-gray-500">
                            {course.credit_hours || course.creditHours} CH
                          </span>
                        </div>

                        {/* Quick Report Access Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewCompleteReport(course);
                          }}
                          className="mt-3 w-full flex items-center justify-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                        >
                          <SafeIcon icon={FiFileText} className="w-4 h-4" />
                          <span>View Complete Report</span>
                        </button>
                      </motion.div>
                    ))
                  ) : (
                    <div className="col-span-full text-center py-8">
                      <SafeIcon icon={FiLock} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No finalized courses yet. Complete and finalize a course to see it here.</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {courses.length === 0 && (
          <div className="text-center py-8 mt-4">
            <SafeIcon icon={FiBook} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No courses created yet. Create your first course to get started.</p>
          </div>
        )}

        {/* Course Status Legend */}
        <div className="mt-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-3">Course Status Guide</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-3 rounded-lg border border-yellow-200">
              <div className="flex items-center mb-2">
                <SafeIcon icon={FiEdit3} className="w-4 h-4 text-yellow-600 mr-2" />
                <h4 className="font-medium text-yellow-800">Draft Course</h4>
              </div>
              <p className="text-sm text-gray-600">
                A course in development. Complete CLO-PLO mapping and target settings, then activate it to make it available for marks entry.
              </p>
            </div>
            <div className="bg-white p-3 rounded-lg border border-green-200">
              <div className="flex items-center mb-2">
                <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-600 mr-2" />
                <h4 className="font-medium text-green-800">Active Course</h4>
              </div>
              <p className="text-sm text-gray-600">
                A course that has been activated and is available for marks entry by users. CLO-PLO mappings and targets are set.
              </p>
            </div>
            <div className="bg-white p-3 rounded-lg border border-purple-200">
              <div className="flex items-center mb-2">
                <SafeIcon icon={FiLock} className="w-4 h-4 text-purple-600 mr-2" />
                <h4 className="font-medium text-purple-800">Finalized Course</h4>
              </div>
              <p className="text-sm text-gray-600">
                A course that has been completed and locked. No further edits are allowed, but reports can be viewed and downloaded.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {showPdfPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                {showPdfPreview.course_name || showPdfPreview.courseName} Report
              </h3>
              <button 
                onClick={() => setShowPdfPreview(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {showPdfPreview.complete_report ? (
                <div 
                  className="w-full h-full"
                  dangerouslySetInnerHTML={{ 
                    __html: `
                    <iframe 
                      srcDoc="${
                        showPdfPreview.complete_report
                          .replace(/"/g, '&quot;')
                          .replace(/</g, '&lt;')
                          .replace(/>/g, '&gt;')
                      }" 
                      style="width: 100%; height: 70vh; border: none;"
                    ></iframe>
                    `
                  }}
                ></div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">No report data available for this course.</p>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button 
                onClick={() => setShowPdfPreview(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Course Modal */}
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Duplicate Course</h3>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-600 mb-4">
                You're duplicating the course <span className="font-medium">{courseToDuplicate?.course_name || courseToDuplicate?.courseName}</span>. 
                Please provide the following information.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Course Name
                </label>
                <input
                  type="text"
                  value={duplicateCourseName}
                  onChange={(e) => setDuplicateCourseName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter course name"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Creator Name
                </label>
                <input
                  type="text"
                  value={duplicateCreatorName}
                  onChange={(e) => setDuplicateCreatorName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter creator name"
                />
              </div>
              <div className="flex items-center justify-between mt-6">
                <button
                  onClick={() => {
                    setShowDuplicateModal(false);
                    setCourseToDuplicate(null);
                    setDuplicateCourseName('');
                    setDuplicateCreatorName('');
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDuplicateCourse}
                  disabled={!duplicateCourseName.trim() || duplicateLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {duplicateLoading ? 'Duplicating...' : 'Duplicate Course'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  )
}

export default CourseSelector